import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react'
import { toast } from 'react-toastify'

import {
  Botao,
  FormRef,
  FuncoesMoeda,
  MoedaInputUnform,
  NumeroInputUnform,
  RadioInputUnform,
  SelectUnform,
  Spacer
} from 'src/componentes'
import { ModalBase } from 'src/componentes/modal-base'
import { SelectOpcao } from 'src/componentes/unform'
import { FormUnform } from 'src/componentes/unform/form'
import {
  DestinoFormaPagamento,
  FormaPagamentoPorExtenso,
  toleranciaDeArredondamento
} from 'src/tipos'

import { MAXIMO_PARCELAS, validarQuantidadeParcelas } from '../limite-parcelas'
import { OpcaoPagamentoLinha } from '../tipos'
import { AcoesModal, Corpo, Resumo } from './modal-incluir-opcao.styles'

/**
 * Como o consultor prefere informar os valores. O backend sempre recebe os
 * três campos (valor, quantidadeParcelas, valorParcela) — aqui só mudamos qual
 * deles é digitado e qual é calculado, porque na negociação às vezes se fecha
 * "o total" e às vezes "a parcela".
 */
export enum ModoValor {
  PorTotal = 'PorTotal',
  PorParcela = 'PorParcela'
}

export interface ModalOpcaoRef {
  abrir: (opcao?: OpcaoPagamentoLinha) => void
  fechar: () => void
}

interface ModalOpcaoProps {
  backdrop?: boolean
  /** Controla se o destino "Entrada" é oferecido. */
  temEntrada: boolean
  /** Duplas (destino + forma) já usadas, para impedir repetição. */
  opcoesExistentes: OpcaoPagamentoLinha[]
  acaoPrimaria: (opcao: OpcaoPagamentoLinha) => void
}

interface DadosFormulario {
  destino: string
  formaPagamento: string
  modoValor: string
  valor: number
  valorParcela: number
  quantidadeParcelas: number
}

const OPCOES_FORMA_PAGAMENTO: SelectOpcao[] = [
  { id: 'Boleto', texto: FormaPagamentoPorExtenso.Boleto },
  { id: 'CartaoCredito', texto: FormaPagamentoPorExtenso.CartaoCredito },
  { id: 'Pix', texto: FormaPagamentoPorExtenso.Pix },
  { id: 'Recorrente', texto: FormaPagamentoPorExtenso.Recorrente }
]

/**
 * A entrada é paga no ato, então só aceita as formas de pagamento à vista:
 * boleto e Pix. Cartão e recorrência são do restante do valor, onde o
 * parcelamento faz sentido.
 */
const FORMAS_DA_ENTRADA = ['Boleto', 'Pix']

const formasDoDestino = (destino: string): SelectOpcao[] =>
  destino === DestinoFormaPagamento.Entrada
    ? OPCOES_FORMA_PAGAMENTO.filter(f => FORMAS_DA_ENTRADA.includes(f.id))
    : OPCOES_FORMA_PAGAMENTO

const Modal: React.ForwardRefRenderFunction<ModalOpcaoRef, ModalOpcaoProps> = (
  { backdrop, temEntrada, opcoesExistentes, acaoPrimaria },
  ref
) => {
  const [aberta, definirAberta] = useState(false)
  const [opcao, definirOpcao] = useState<OpcaoPagamentoLinha>(null)
  const [modoValor, definirModoValor] = useState<string>(ModoValor.PorTotal)
  const [destinoSelecionado, definirDestinoSelecionado] = useState<string>(null)
  const formRef = useRef<FormRef>(null)

  // Sem essa key o Unform retém valores de aberturas anteriores do modal.
  const [chaveFormulario, definirChaveFormulario] = useState(0)

  const ehEdicao = !!opcao

  /**
   * A entrada é paga no ato: é sempre uma parcela só. O campo fica travado em 1
   * para o consultor não cadastrar um parcelamento que a tela do aluno não sabe
   * cobrar — ali a entrada aparece como valor único, pago hoje.
   */
  const ehEntrada = destinoSelecionado === DestinoFormaPagamento.Entrada

  const fechar = () => {
    definirAberta(false)
    definirOpcao(null)
  }

  const abrir = (opcaoSelecionada?: OpcaoPagamentoLinha) => {
    definirOpcao(opcaoSelecionada ?? null)
    definirDestinoSelecionado(opcaoSelecionada?.destino ?? null)
    definirModoValor(ModoValor.PorTotal)
    definirChaveFormulario(chave => chave + 1)
    definirAberta(true)
  }

  useImperativeHandle<ModalOpcaoRef, ModalOpcaoRef>(ref, () => ({
    abrir,
    fechar
  }))

  const opcoesDoDestino = (destino: string) =>
    opcoesExistentes.filter(o => o.destino === destino && o.id !== opcao?.id)

  /**
   * Formas já esgotadas naquele destino.
   *
   * Na **entrada**, uma forma esgota no primeiro cadastro: a entrada é sempre
   * parcela única, então não há um segundo parcelamento a oferecer.
   *
   * No **restante**, a mesma forma pode ter vários parcelamentos (recorrência
   * em 6x, 12x, 21x, 24x), então ela nunca some do combo — o que o domínio
   * recusa é a combinação forma + quantidade repetida, verificada no submit.
   */
  const formasEsgotadasNoDestino = (destino: string) =>
    destino === DestinoFormaPagamento.Entrada
      ? opcoesDoDestino(destino).map(o => o.formaPagamento)
      : []

  const destinoEsgotado = (destino: string) =>
    formasEsgotadasNoDestino(destino).length >= formasDoDestino(destino).length

  const opcoesDestino: SelectOpcao[] = [
    ...(temEntrada
      ? [{ id: DestinoFormaPagamento.Entrada, texto: 'Entrada' }]
      : []),
    { id: DestinoFormaPagamento.Restante, texto: 'Restante' }
  ].filter(d => ehEdicao || !destinoEsgotado(d.id))

  // Só oferece as formas ainda livres para o destino escolhido, em vez de
  // aceitar e recusar depois. Na entrada a lista já vem restrita a boleto e Pix.
  const opcoesFormaDisponiveis = destinoSelecionado
    ? formasDoDestino(destinoSelecionado).filter(
        f => !formasEsgotadasNoDestino(destinoSelecionado).includes(f.id)
      )
    : OPCOES_FORMA_PAGAMENTO

  const acaoSucesso = async (dados: DadosFormulario) => {
    const destino = (dados.destino ?? opcao?.destino) as DestinoFormaPagamento
    const formaPagamento = dados.formaPagamento ?? opcao?.formaPagamento

    // A entrada e sempre parcela unica. Forcado aqui, e nao so no campo: input
    // desabilitado nao e fonte confiavel do que foi submetido.
    const quantidadeParcelas =
      destino === DestinoFormaPagamento.Entrada
        ? 1
        : Number(dados.quantidadeParcelas)

    if (!destino || !formaPagamento) {
      toast('Informe o destino e a forma de pagamento.', { type: 'info' })
      return
    }

    const erroQuantidade = validarQuantidadeParcelas(destino, quantidadeParcelas)

    if (erroQuantidade) {
      // Erro no próprio campo (borda vermelha e mensagem abaixo), e não num toast:
      // o consultor vê qual campo corrigir sem procurar o aviso.
      formRef.current?.setFieldError('quantidadeParcelas', erroQuantidade)
      return
    }

    // A mesma forma pode ter vários parcelamentos, mas não o mesmo duas vezes:
    // seriam dois cartões idênticos na tela do aluno. O domínio também recusa;
    // aqui a mensagem chega antes, sem ida ao servidor.
    const jaExiste = opcoesExistentes.some(
      o =>
        o.id !== opcao?.id &&
        o.destino === destino &&
        o.formaPagamento === formaPagamento &&
        o.quantidadeParcelas === quantidadeParcelas
    )

    if (jaExiste) {
      toast(
        `Já existe uma opção de ${FormaPagamentoPorExtenso[
          formaPagamento as keyof typeof FormaPagamentoPorExtenso
        ] ?? formaPagamento} em ${quantidadeParcelas}x para esse destino.`,
        { type: 'info' }
      )
      return
    }

    // Uma das duas é digitada; a outra é derivada. Assim os três campos que o
    // backend espera sempre fecham entre si.
    let valor: number
    let valorParcela: number

    if (dados.modoValor === ModoValor.PorParcela) {
      valorParcela = Number(dados.valorParcela)

      if (!valorParcela || valorParcela <= 0) {
        toast('Informe o valor da parcela.', { type: 'info' })
        return
      }

      valor = Number((valorParcela * quantidadeParcelas).toFixed(2))
    } else {
      valor = Number(dados.valor)

      if (!valor || valor <= 0) {
        toast('Informe o valor total.', { type: 'info' })
        return
      }

      valorParcela = Number((valor / quantidadeParcelas).toFixed(2))
    }

    // Mesma invariante do backend (PlanoPagamentoNegociado.ValidarComposicao).
    // Só pode falhar se o arredondamento de centavos estourar a tolerância.
    if (
      Math.abs(quantidadeParcelas * valorParcela - valor) >
      toleranciaDeArredondamento(quantidadeParcelas)
    ) {
      toast(
        `${quantidadeParcelas} x ${FuncoesMoeda.formatarMoeda(
          valorParcela
        )} não fecha com o total ${FuncoesMoeda.formatarMoeda(
          valor
        )}. Ajuste os valores.`,
        { type: 'info' }
      )
      return
    }

    acaoPrimaria({
      id: opcao?.id ?? null,
      // Mantém o Id do servidor: sem ele a edição recriaria a opção.
      opcaoId: opcao?.opcaoId,
      destino,
      formaPagamento,
      valor,
      quantidadeParcelas,
      valorParcela
    })

    fechar()
  }

  return (
    <ModalBase aberta={aberta} backdrop={backdrop} id="modal-opcao-pagamento">
      <Corpo>
        <h5>{ehEdicao ? 'Editar opção de pagamento' : 'Adicionar opção de pagamento'}</h5>
        <FormUnform
          ref={formRef}
          key={chaveFormulario}
          dadosIniciais={
            opcao
              ? { ...opcao, modoValor: ModoValor.PorTotal }
              : {
                  modoValor: ModoValor.PorTotal,
                  ...(ehEntrada ? { quantidadeParcelas: 1 } : {})
                }
          }
          acaoSucesso={acaoSucesso}
        >
          <SelectUnform
            id="slct_destino_opcao"
            name="destino"
            label="Destino"
            placeholder="Selecione"
            obrigatorio
            disabled={ehEdicao}
            valorAlterado={escolha =>
              definirDestinoSelecionado(escolha?.id ?? null)
            }
            opcoes={opcoesDestino}
          />
          <SelectUnform
            key={`forma-${destinoSelecionado ?? 'sem-destino'}`}
            id="slct_forma_opcao"
            name="formaPagamento"
            label="Forma de pagamento"
            placeholder={
              destinoSelecionado ? 'Selecione' : 'Escolha o destino primeiro'
            }
            obrigatorio
            disabled={ehEdicao || !destinoSelecionado}
            opcoes={opcoesFormaDisponiveis}
          />

          {destinoSelecionado && !ehEdicao && !opcoesFormaDisponiveis.length && (
            <Resumo>
              Todas as formas de pagamento já foram cadastradas para a entrada.
              Edite ou exclua uma das opções existentes.
            </Resumo>
          )}

          <Spacer padding="8px 0px" />

          <RadioInputUnform
            opcoesHorizontal
            id="radio_modo_valor"
            name="modoValor"
            valorAlterado={definirModoValor}
            opcoes={[
              {
                id: `modo-${ModoValor.PorTotal}`,
                texto: 'Informar valor total',
                valor: ModoValor.PorTotal
              },
              {
                id: `modo-${ModoValor.PorParcela}`,
                texto: 'Informar valor da parcela',
                valor: ModoValor.PorParcela
              }
            ]}
          />

          <Spacer padding="8px 0px" />

          <NumeroInputUnform
            /*
             * A key remonta o campo quando o destino muda: sem ela o valor
             * digitado antes de escolher "Entrada" continuaria no input, agora
             * desabilitado e divergente do 1 que será enviado.
             */
            key={`parcelas-${destinoSelecionado ?? 'sem-destino'}`}
            id="ipt_qtd_parcelas_opcao"
            name="quantidadeParcelas"
            max={ehEntrada ? 1 : MAXIMO_PARCELAS}
            maxLength={2}
            label="Quantidade de parcelas"
            obrigatorio
            disabled={ehEntrada}
          />

          {ehEntrada && (
            <Resumo>A entrada é paga de uma vez, em parcela única.</Resumo>
          )}

          {modoValor === ModoValor.PorParcela ? (
            <MoedaInputUnform
              id="ipt_valor_parcela_opcao"
              name="valorParcela"
              label="Valor da parcela"
              obrigatorio
            />
          ) : (
            <MoedaInputUnform
              id="ipt_valor_total_opcao"
              name="valor"
              label="Valor total"
              obrigatorio
            />
          )}

          <Resumo>
            {modoValor === ModoValor.PorParcela
              ? 'O valor total é calculado: parcela x quantidade.'
              : 'O valor da parcela é calculado: total ÷ quantidade.'}
          </Resumo>

          <Spacer padding="10px 0px" />
          <AcoesModal>
            <Botao
              type="button"
              id="btn-cancelar-opcao"
              texto="Cancelar"
              tema="Secundario"
              onClick={fechar}
            />
            <Botao
              id="btn-adicionar-opcao"
              texto={ehEdicao ? 'Salvar' : 'Adicionar'}
              type="submit"
            />
          </AcoesModal>
        </FormUnform>
      </Corpo>
    </ModalBase>
  )
}

export const ModalOpcaoPagamento = forwardRef(Modal)
